<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-body class="p-50">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-50 font-weight-bold text-nowrap">
            Cash in Hand:
          </label>
          <span class="amount-display mr-50">
            ₹{{ formatAmount(cashData.total_cash) }}
          </span>
          <div class="date-picker-wrapper mr-50">
            <flat-picker
              :value="selectedDate"
              placeholder="Select Date"
              @input="handleDateChange"
            />
          </div>

          <b-dropdown
            ref="cashDropdown"
            variant="link"
            no-caret
            right
            no-flip
            boundary="viewport"
            toggle-class="p-0 text-decoration-none"
            menu-class="dropdown-menu-custom"
            :no-close-on-select="true"
            @show="dropdownOpen = true"
            @hide="dropdownOpen = false"
          >
            <template #button-content>
              <feather-icon
                :icon="dropdownOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="20"
                class="text-primary"
              />
            </template>

            <div
              class="dropdown-content-wrapper"
              @click.stop
              @mousedown.stop
            >
              <div class="cash-details-section">
                <div class="detail-row">
                  <span class="detail-label">Opening Balance:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.opening_balance) }}</span>
                </div>

                <div class="detail-row positive-row">
                  <span class="detail-label">Payment Received:</span>
                  <span class="detail-value text-success">₹{{ formatAmount(totalPaymentReceived) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_cash"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- Cash:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_cash) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_upi"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- UPI:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_upi) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_cheque"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- Cheque:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_cheque) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_rgts"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- RTGS:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_rgts) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_credit_note"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- Credit Note:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_credit_note) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_challan"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- Challan:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_by_challan) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_rec_by_acc_dept"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- Rec By Acc. Dept:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_as_rec_by_acc_dept) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_mr_handover"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- MR Handover:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_as_mr_handover) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_loan"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">- As Loan:</span>
                  <span class="detail-value">₹{{ formatAmount(cashData.payment_received_as_loan) }}</span>
                </div>

                <div class="detail-row loan-section-header">
                  <span class="detail-label font-weight-bold">Loan:</span>
                  <span class="detail-value">₹{{ formatAmount(netLoan) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_cash"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Cash):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_cash) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_upi"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (UPI):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_upi) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_cheque"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Cheque):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_cheque) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_rgts"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (RTGS):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_rgts) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_credit_note"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Credit Note):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_credit_note) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_challan"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Challan):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_challan) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_transferred_to_salary_advance"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Transferred to Salary Adv.:</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_transferred_to_salary_advance) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_transferred_to_salary_advance"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Transferred to Medicine Adv.:</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.loan_received_by_transferred_to_medicine_advance) }}</span>
                </div>
                <div
                  v-if="cashData.loans_taken"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Taken:</span>
                  <span class="detail-value text-danger">₹{{ formatAmount(cashData.loans_taken) }}</span>
                </div>

                <div class="detail-row loan-section-header">
                  <span class="detail-label font-weight-bold">MR Loan:</span>
                  <span class="detail-value">₹{{ formatAmount(netMrLoan) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loan_received_by_cash"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Cash):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.mr_loan_received_by_cash) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loan_received_by_upi"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (UPI):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.mr_loan_received_by_upi) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loan_received_by_cheque"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Cheque):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.mr_loan_received_by_cheque) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loan_received_by_rgts"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (RTGS):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.mr_loan_received_by_rgts) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loan_received_by_credit_note"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Received (Credit Note):</span>
                  <span class="detail-value text-success">₹{{ formatAmount(cashData.mr_loan_received_by_credit_note) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_mr_handover"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Taken (As Handover):</span>
                  <span class="detail-value text-danger">₹{{ formatAmount(cashData.payment_received_as_mr_handover) }}</span>
                </div>
                <div
                  v-if="cashData.mr_loans_taken"
                  class="detail-row sub-row"
                >
                  <span class="detail-label pl-1">Taken (Total Loan):</span>
                  <span class="detail-value text-danger">₹{{ formatAmount(cashData.mr_loans_taken) }}</span>
                </div>
                <div class="detail-row negative-row">
                  <span class="detail-label">Expenses:</span>
                  <span class="detail-value text-danger">₹{{ formatAmount(cashData.expenses) }}</span>
                </div>
              </div>
            </div>
          </b-dropdown>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BDropdown,
} from 'bootstrap-vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import dateMixin from '@/mixins/dateMixin'

export default {
  name: 'DailyLiveCashInfo',
  components: {
    BCard,
    BCardBody,
    BDropdown,
    FlatPicker,
  },
  mixins: [dateMixin],
  props: {
    cashData: {
      type: Object,
      required: true,
      default: () => ({
        date: '',
        total_cash: 0.0,
        opening_balance: 0.0,
        payment_received_by_cash: 0.0,
        payment_received_by_upi: 0.0,
        payment_received_by_cheque: 0.0,
        payment_received_by_rgts: 0.0,
        payment_received_by_credit_note: 0.0,
        payment_received_by_challan: 0.0,
        payment_received_as_mr_handover: 0.0,
        payment_received_as_loan: 0.0,
        payment_received_as_rec_by_acc_dept: 0.0,
        loan_received_by_cash: 0.0,
        loan_received_by_upi: 0.0,
        loan_received_by_cheque: 0.0,
        loan_received_by_rgts: 0.0,
        loan_received_by_credit_note: 0.0,
        loan_received_by_challan: 0.0,
        loan_received_by_transferred_to_salary_advance: 0.0,
        loan_received_by_transferred_to_medicine_advance: 0.0,
        loans_taken: 0.0,
        mr_loan_received_by_cash: 0.0,
        mr_loan_received_by_upi: 0.0,
        mr_loan_received_by_cheque: 0.0,
        mr_loan_received_by_rgts: 0.0,
        expenses: 0.0,
      }),
    },
    selectedDate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dropdownOpen: false,
    }
  },
  computed: {
    totalPaymentReceived() {
      return (
        (this.cashData.payment_received_by_cash || 0)
        + (this.cashData.payment_received_by_upi || 0)
        + (this.cashData.payment_received_by_cheque || 0)
        + (this.cashData.payment_received_by_rgts || 0)
        + (this.cashData.payment_received_by_credit_note || 0)
        + (this.cashData.payment_received_by_challan || 0)
        + (this.cashData.payment_received_as_mr_handover || 0)
        + (this.cashData.payment_received_as_loan || 0)
        + (this.cashData.payment_received_as_rec_by_acc_dept || 0)
      )
    },
    totalLoanReceived() {
      return (
        (this.cashData.loan_received_by_cash || 0)
        + (this.cashData.loan_received_by_upi || 0)
        + (this.cashData.loan_received_by_cheque || 0)
        + (this.cashData.loan_received_by_rgts || 0)
        + (this.cashData.loan_received_by_credit_note || 0)
        + (this.cashData.loan_received_by_challan || 0)
        + (this.cashData.loan_received_by_transferred_to_salary_advance || 0)
        + (this.cashData.loan_received_by_transferred_to_medicine_advance || 0)
      )
    },
    totalMrLoanReceived() {
      return (
        (this.cashData.mr_loan_received_by_cash || 0)
        + (this.cashData.mr_loan_received_by_upi || 0)
        + (this.cashData.mr_loan_received_by_cheque || 0)
        + (this.cashData.mr_loan_received_by_rgts || 0)
      )
    },
    netLoan() {
      return this.totalLoanReceived - (this.cashData.loans_taken || 0)
    },
    netMrLoan() {
      return this.totalMrLoanReceived - (this.cashData.payment_received_as_mr_handover || 0)
    },
  },
  methods: {
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    handleDateChange(newDate) {
      // Convert the date to YYYY-MM-DD format before emitting
      const formattedDate = this.formatedDate(new Date(newDate), 'YYYY-MM-DD')
      this.$emit('date-changed', formattedDate)
    },
  },
}
</script>

<style scoped>
.p-50 {
  padding: 0.4rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.amount-display {
  font-size: 1.1rem;
  font-weight: bold;
  color: #2c5f2d;
  min-width: 98px;
}

.date-picker-wrapper {
  max-width: 150px;
}

.date-picker-wrapper ::v-deep .form-control {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  height: auto;
}

.dropdown-content-wrapper {
  padding: 0;
  margin-left: -4px;
}

.cash-details-section {
  padding: 0.5rem;
  min-width: 320px;
  max-width: 320px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.detail-row:last-child {
  border-bottom: none;
}

.loan-section-header {
  background-color: #f0f0f0;
  font-weight: 600;
}

.sub-row {
  padding: 0.25rem 0.5rem;
  background-color: #f8f8f8;
}

.sub-row .detail-label {
  font-size: 0.8rem;
  color: #9c9aa6;
}

.sub-row .detail-value {
  font-size: 0.8rem;
  font-weight: 500;
}

.pl-1 {
  padding-left: 0.5rem;
}

.detail-label {
  color: #6e6b7b;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 600;
  font-size: 0.875rem;
  color: #5e5873;
}

::v-deep .dropdown-menu-custom {
  padding: 0 !important;
  min-width: 320px !important;
  max-width: 350px !important;
  max-height: 70vh;
  overflow-y: auto;
}
</style>
